import { useState, useRef } from "react";
import { Paperclip, X, ImageIcon } from "lucide-react";

const MAX_IMAGES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ImageAttachPicker = ({ attachments, setAttachments, disabled = false }) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_IMAGES - attachments.length;
    if (remaining <= 0) return;

    const filesToProcess = files.slice(0, remaining);

    filesToProcess.forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > MAX_FILE_SIZE) return;

      const reader = new FileReader();
      reader.onload = () => {
        setAttachments((prev) => {
          if (prev.length >= MAX_IMAGES) return prev;
          return [
            ...prev,
            {
              filename: file.name,
              data: reader.result,
              preview: reader.result,
            },
          ];
        });
      };
      reader.readAsDataURL(file);
    });

    // Reset input so same file can be re-selected
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div data-testid="image-attach-picker">
      {/* Thumbnails */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map((att, i) => (
            <div
              key={i}
              className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/20 group"
              data-testid={`attachment-preview-${i}`}
            >
              <img
                src={att.preview}
                alt={att.filename}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeAttachment(i)}
                className="absolute top-0 right-0 w-5 h-5 bg-red-500 rounded-bl-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-attachment-${i}`}
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Attach button */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled || attachments.length >= MAX_IMAGES}
        className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded transition-colors ${
          attachments.length >= MAX_IMAGES
            ? "text-white/20 cursor-not-allowed"
            : "text-white/50 hover:text-white/80 hover:bg-white/5"
        }`}
        data-testid="attach-images-btn"
      >
        <Paperclip className="w-3.5 h-3.5" />
        {attachments.length > 0
          ? `${attachments.length}/${MAX_IMAGES}`
          : "Attach images"}
      </button>
    </div>
  );
};

export default ImageAttachPicker;
